// Dashboard period model (WEB-6581). The Dashboard's date control is reconciled
// with Shadow Billing's chip bar, but with a Dashboard-appropriate choice set:
// the 2 most recent quarters, the 4 most recent months, and Custom. Pure / no
// React — unit-testable. Months/quarters resolve to absolute calendar windows
// (a current-month chip is the full calendar month, matching the prior
// FinRangePicker behavior so the "% of forecast" framing is unchanged).
import type { DateRange, PeriodOption } from '../detail/periodRange'

export type { DateRange, PeriodOption }

const pad2 = (n: number): string => String(n).padStart(2, '0')
const isoDate = (y: number, m: number, d: number): string => `${y}-${pad2(m)}-${pad2(d)}`
/** Last day-of-month for a 1-based month. */
const lastDay = (y: number, m: number): number => new Date(y, m, 0).getDate()

type Quarter = 1 | 2 | 3 | 4
/** 1-based quarter for a 0-based month. */
const quarterOf = (month0: number): Quarter => (Math.floor(month0 / 3) + 1) as Quarter

const quarterSpan = (year: number, q: Quarter): DateRange => {
  const startMonth = (q - 1) * 3 + 1
  const endMonth = startMonth + 2
  return { from: isoDate(year, startMonth, 1), to: isoDate(year, endMonth, lastDay(year, endMonth)) }
}

/** The (year, quarter) one step before the given one (handles year rollover). */
const prevQuarter = (year: number, q: Quarter): { year: number; q: Quarter } =>
  q === 1 ? { year: year - 1, q: 4 } : { year, q: (q - 1) as Quarter }

const quarterKey = (year: number, q: Quarter): string => `q-${year}-${q}`
const quarterLabel = (year: number, q: Quarter): string => `Q${q} '${pad2(year % 100)}`
const monthKey = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`

const QUARTER_KEY = /^q-(\d{4})-([1-4])$/
const MONTH_KEY = /^(\d{4})-(\d{2})$/

/**
 * The 2 most recent quarters (previous + current), then the 4 most recent
 * months (oldest→newest, abbreviated), then Custom. Deterministic under `today`.
 */
export const buildDashboardPeriodOptions = (today: Date): PeriodOption[] => {
  const y = today.getFullYear()
  const q = quarterOf(today.getMonth())
  const pq = prevQuarter(y, q)
  const quarters: PeriodOption[] = [
    { key: quarterKey(pq.year, pq.q), label: quarterLabel(pq.year, pq.q) },
    { key: quarterKey(y, q), label: quarterLabel(y, q) }
  ]
  const months: PeriodOption[] = []
  for (let i = 3; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1)
    months.push({ key: monthKey(d), label: d.toLocaleString('en-US', { month: 'short' }) })
  }
  return [...quarters, ...months, { key: 'custom', label: 'Custom' }]
}

/** The default selection — the current month (most recent month chip). */
export const defaultDashboardPeriodKey = (today: Date): string => monthKey(today)

/**
 * Resolve a Dashboard period key to {from,to}. Quarters and YYYY-MM months
 * resolve to their full calendar window; Custom uses the supplied range, or null
 * when incomplete (so the portfolio query is skipped).
 */
export const resolveDashboardPeriod = (key: string, customRange?: DateRange | null): DateRange | null => {
  if (key === 'custom') {
    return customRange != null && customRange.from !== '' && customRange.to !== '' ? customRange : null
  }
  const qm = QUARTER_KEY.exec(key)
  if (qm != null) return quarterSpan(Number(qm[1]), Number(qm[2]) as Quarter)
  const mm = MONTH_KEY.exec(key)
  if (mm != null) {
    const year = Number(mm[1])
    const month = Number(mm[2]) // 1-based
    return { from: isoDate(year, month, 1), to: isoDate(year, month, lastDay(year, month)) }
  }
  return null
}

/**
 * The period-over-period comparison window for a key (WEB-6578 deltas): the
 * prior quarter for a quarter, the prior month for a month, null for Custom.
 */
export const resolveDashboardPriorPeriod = (key: string): DateRange | null => {
  const qm = QUARTER_KEY.exec(key)
  if (qm != null) {
    const p = prevQuarter(Number(qm[1]), Number(qm[2]) as Quarter)
    return quarterSpan(p.year, p.q)
  }
  const mm = MONTH_KEY.exec(key)
  if (mm != null) {
    const my = Number(mm[1])
    const mo = Number(mm[2])
    const pm = mo === 1 ? 12 : mo - 1
    const py = mo === 1 ? my - 1 : my
    return { from: isoDate(py, pm, 1), to: isoDate(py, pm, lastDay(py, pm)) }
  }
  return null
}

/** True for a quarter key (vs a month) — drives the delta tooltip wording. */
export const isQuarterKey = (key: string): boolean => QUARTER_KEY.test(key)

export const isMonthPeriodKey = (key: string): boolean => MONTH_KEY.test(key)

/** Splits the Dashboard period option list into quarter, month, and Custom buckets. */
export const partitionPeriodOptions = (
  periods: PeriodOption[]
): { quarters: PeriodOption[]; months: PeriodOption[]; custom: PeriodOption | null } => {
  const quarters: PeriodOption[] = []
  const months: PeriodOption[] = []
  let custom: PeriodOption | null = null
  for (const p of periods) {
    if (p.key === 'custom') {
      custom = p
    } else if (QUARTER_KEY.test(p.key)) {
      quarters.push(p)
    } else if (MONTH_KEY.test(p.key)) {
      months.push(p)
    }
  }
  return { quarters, months, custom }
}
